import React, { useState, useEffect, useRef } from 'react';
import { api } from '../constants';
import Toast from '../components/Toast';
import Modal from '../components/Modal';

interface ScanFile {
    key: string;
    fileName: string;
    lastModified: string;
    size: number;
    downloadUrl: string;
}

const Scans: React.FC = () => {
    const [files, setFiles] = useState<ScanFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        fileName: string;
    }>({
        isOpen: false,
        fileName: ''
    });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
        setNotification({ message, type });
    };

    const baseUrl = (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, '');
    const token = localStorage.getItem('auth_token');

    const fetchFiles = async () => {
        try {
            const res = await fetch(`${baseUrl}/scans`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch scans');
            const data = await res.json();
            setFiles(data);
        } catch (err: any) {
            showToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            // 1. Get Pre-signed URL
            const initRes = await fetch(`${baseUrl}/scans/upload`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    fileName: file.name, 
                    contentType: file.type || 'application/octet-stream' 
                })
            });
            
            if (!initRes.ok) throw new Error('Failed to initiate upload');
            const { uploadUrl } = await initRes.json();

            // 2. Upload to S3
            await fetch(uploadUrl, {
                method: 'PUT',
                body: file
            });

            showToast(`File ${file.name} uploaded successfully.`);
            // 3. Refresh list
            await fetchFiles();
        } catch (err: any) {
            showToast(err.message || 'Upload failed', 'error');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (fileName: string) => {
        setConfirmModal({ isOpen: true, fileName });
    };

    const confirmDelete = async () => {
        const fileName = confirmModal.fileName;
        try {
            const res = await fetch(`${baseUrl}/scans/${fileName}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!res.ok) throw new Error('Delete failed');
            
            showToast('File deleted successfully.');
            setFiles(files.filter(f => f.fileName !== fileName));
        } catch (err: any) {
            showToast(err.message, 'error');
        }
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                <div className="flex justify-between items-center mb-10">
                    <div>
                        <h1 className="text-4xl font-black text-slate-900 tracking-tight">Intraoral Scans</h1>
                        <p className="text-slate-500 font-medium mt-2">Securely upload and manage your patient scan data.</p>
                    </div>
                    <button 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all hover:-translate-y-1 disabled:opacity-50 disabled:transform-none flex items-center gap-3"
                    >
                        {uploading ? (
                            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Uploading...</>
                        ) : (
                            <><span>⬆</span> Upload Scan Data</>
                        )}
                    </button>
                    <input type="file" ref={fileInputRef} className="hidden" onChange={handleUpload} />
                </div>

                {notification && (
                    <Toast 
                        message={notification.message} 
                        type={notification.type} 
                        onClose={() => setNotification(null)} 
                    />
                )}

                <Modal 
                    isOpen={confirmModal.isOpen}
                    title="Delete Scan File"
                    message={`Are you sure you want to permanently delete "${confirmModal.fileName}"? This action cannot be undone.`}
                    type="danger"
                    onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                    onConfirm={confirmDelete}
                />

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin h-10 w-10 border-4 border-slate-200 border-t-blue-600 rounded-full"></div>
                    </div>
                ) : files.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] shadow-xl border border-slate-100">
                        <div className="text-6xl mb-4 opacity-20">📂</div>
                        <h3 className="text-xl font-black text-slate-900">No Scans Found</h3>
                        <p className="text-slate-400 mt-2">Upload your first intraoral scan file to get started.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 overflow-hidden">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="p-6 text-xs font-black text-slate-400 uppercase tracking-widest">File Name</th>
                                    <th className="p-6 text-xs font-black text-slate-400 uppercase tracking-widest">Date Uploaded</th>
                                    <th className="p-6 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Size</th>
                                    <th className="p-6 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {files.map((file) => (
                                    <tr key={file.key} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="p-6 font-bold text-slate-700 flex items-center gap-3">
                                            <span className="text-2xl">📄</span>
                                            {file.fileName}
                                        </td>
                                        <td className="p-6 text-sm font-medium text-slate-500">
                                            {new Date(file.lastModified).toLocaleDateString()} <span className="opacity-50 text-xs">{new Date(file.lastModified).toLocaleTimeString()}</span>
                                        </td>
                                        <td className="p-6 text-sm font-mono text-slate-400 text-right">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </td>
                                        <td className="p-6 text-right">
                                            <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <a 
                                                    href={file.downloadUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-blue-100 transition-colors"
                                                >
                                                    Download
                                                </a>
                                                <button 
                                                    onClick={() => handleDelete(file.fileName)}
                                                    className="px-4 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-red-100 transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Scans;
